import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, IconButton } from '@material-ui/core'
import Card from '../../../../../molecules/Card'
import SydLabel from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import SydSelect from '../../../../../commonDesign/SydSelect'
import SydButton from '../../../../../commonDesign/Button'
import SydModal from '../../../../../commonDesign/SydModal'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import Icon from '../../../../../atoms/Icon'
import { ICON_NAMES } from '../../../../../../constants'
import { decipherError } from '../../../../../../utils/decipherError'
import {
  COLUMN_TYPE_OPTIONS,
  sectionHeaderStyle,
  rowStyle,
  errorStyle,
  emptyStyle,
  descriptionStyle,
  getDefinition,
  useSaveDefinitionSection
} from './shared'

function EditAssociationsModal ({ open, onClose, target }) {
  const saveSection = useSaveDefinitionSection(target)
  const [rows, setRows] = useState([])
  const [error, setError] = useState(null)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    if (open) {
      const def = getDefinition(target)
      setRows((def.associations || []).map(a => ({
        name: a.name || '',
        type: a.type || 'string',
        length: a.length ?? null,
        path: Array.isArray(a.path) ? a.path.join(', ') : (a.path || '')
      })))
      setError(null)
    }
  }, [open, target])

  const handleChange = useCallback((i, field, value) => {
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r))
  }, [])

  const handleAdd = useCallback(() => {
    setRows(prev => [...prev, { name: '', type: 'string', length: null, path: '' }])
  }, [])

  const handleRemove = useCallback((i) => {
    setRows(prev => prev.filter((_, idx) => idx !== i))
  }, [])

  const handleSave = useCallback(async () => {
    for (const r of rows) { if (!r.name) { setError('All associations must have a name'); return } }
    const def = getDefinition(target)
    if (rows.length && !def.changeTracking) {
      setError('Change tracking must be enabled before adding associations')
      return
    }
    const associations = rows.map(a => {
      const pathStr = (a.path || '').trim()
      let path = null
      if (pathStr) {
        const parts = pathStr.split(',').map(s => s.trim()).filter(Boolean)
        path = parts.length === 1 ? parts[0] : parts
      }
      return {
        name: a.name,
        type: a.type,
        length: a.length ? Number(a.length) : null,
        path
      }
    })
    try {
      setError(null)
      setProcessing(true)
      await saveSection({ associations })
      onClose?.()
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [rows, saveSection, onClose, target])

  return (
    <SydModal open={open} onClose={onClose} title='Edit Associations' size='large'>
      <div style={descriptionStyle}>
        Virtual associations create a separate child table for one-to-many relationships extracted from the JSON data.
        Each association becomes a table named <code>{'{tableName}'}_{'{name}'}</code> with its own primary key.
        Change tracking must be enabled before associations can be added.
        The path expression points to the array field in the JSON data, or use comma-separated paths for nested extraction.
      </div>
      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'flex-end' }}>
        <SydButton variant='outline' size='sm' icon='add' onClick={handleAdd}>Add Association</SydButton>
      </div>
      {rows.map((item, i) => (
        <div key={i} style={rowStyle}>
          <Grid container spacing={1} alignItems='flex-end'>
            <Grid item xs={3}>
              <SydLabel label='Name'>
                <SydInput size='sm' value={item.name} onChange={e => handleChange(i, 'name', e.target.value)} placeholder='assoc_name' minWidth='0' />
              </SydLabel>
            </Grid>
            <Grid item xs={2}>
              <SydLabel label='Type'>
                <SydSelect size='sm' options={COLUMN_TYPE_OPTIONS} value={item.type} onChange={e => handleChange(i, 'type', e.target.value)} minWidth='0' />
              </SydLabel>
            </Grid>
            <Grid item xs={1}>
              <SydLabel label='Length'>
                <SydInput size='sm' value={item.length ?? ''} onChange={e => handleChange(i, 'length', e.target.value)} minWidth='0' />
              </SydLabel>
            </Grid>
            <Grid item xs={5}>
              <SydLabel label='Path (comma-separated for multiple)'>
                <SydInput size='sm' value={item.path} onChange={e => handleChange(i, 'path', e.target.value)} placeholder='$.field' minWidth='0' />
              </SydLabel>
            </Grid>
            <Grid item xs={1} style={{ textAlign: 'right' }}>
              <IconButton size='small' onClick={() => handleRemove(i)} title='Remove'>
                <Icon name={ICON_NAMES.close} customSize='16px' />
              </IconButton>
            </Grid>
          </Grid>
        </div>
      ))}
      {!rows.length && <div style={emptyStyle}>No associations defined</div>}
      <SydModalActions>
        {error ? <div style={errorStyle}>{error}</div> : null}
        <SydButton disabled={processing} variant='ghost' size='md' onClick={onClose}>Cancel</SydButton>
        <SydButton processing={processing} variant='primary' size='md' onClick={handleSave}>Save</SydButton>
      </SydModalActions>
    </SydModal>
  )
}

EditAssociationsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  target: PropTypes.object
}

export default function AssociationsCard ({ target }) {
  const [editing, setEditing] = useState(false)
  const definition = getDefinition(target)
  const associations = definition.associations || []

  return (
    <Card>
      <div style={sectionHeaderStyle}>
        <h3>Associations ({associations.length})</h3>
        <IconButton size='small' onClick={() => setEditing(true)} title='Edit Associations'>
          <Icon name={ICON_NAMES.edit} customSize='18px' />
        </IconButton>
      </div>
      <table className='opal-table'>
        <thead>
          <tr><th>Name</th><th>Type</th><th>Path</th></tr>
        </thead>
        <tbody>
          {associations.map((a, i) => (
            <tr key={i}>
              <td><code>{a.name}</code></td>
              <td>{a.type}{a.length ? `(${a.length})` : ''}</td>
              <td>{Array.isArray(a.path) ? a.path.join(', ') : (a.path || '—')}</td>
            </tr>
          ))}
          {!associations.length && <tr><td colSpan={3}>No associations</td></tr>}
        </tbody>
      </table>
      <EditAssociationsModal open={editing} onClose={() => setEditing(false)} target={target} />
    </Card>
  )
}

AssociationsCard.propTypes = { target: PropTypes.object.isRequired }
